import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { ChevronDown, Menu, ShoppingBag, X } from 'lucide-react'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { useCart } from '../context/CartContext'
import { useCatalog } from '../context/CatalogContext'
import { useLanguage } from '../context/LanguageContext'

function NavItem({ item, className, onNavigate }) {
  if (item.to) {
    return (
      <Link to={item.to} onClick={onNavigate} className={className}>
        {item.label}
      </Link>
    )
  }

  return (
    <a href={item.href} onClick={onNavigate} className={className}>
      {item.label}
    </a>
  )
}

function NavDropdown({ label, items, onNavigate }) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="nav-link relative inline-flex items-center gap-1 text-sm font-medium text-ng-dark transition-colors hover:text-ng-mid"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 min-w-[12rem] pt-2">
          <div className="rounded-xl border border-ng-dark/10 bg-white py-2 shadow-lg">
            {items.map((item) => (
              <NavItem
                key={item.to || item.href}
                item={item}
                onNavigate={() => {
                  setOpen(false)
                  onNavigate?.()
                }}
                className="block px-4 py-2.5 text-sm font-medium text-ng-dark transition-colors hover:bg-ng-beige hover:text-ng-mid"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function MobileNavGroup({ label, items, onNavigate }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border-b border-ng-dark/5">
      <button
        type="button"
        className="flex w-full items-center justify-between py-3 text-base font-medium text-ng-dark transition-colors hover:text-ng-mid"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
        <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="pb-3 pl-3">
          {items.map((item) => (
            <NavItem
              key={item.to || item.href}
              item={item}
              onNavigate={() => {
                setOpen(false)
                onNavigate?.()
              }}
              className="block py-2.5 text-sm font-medium text-ng-dark/80 transition-colors hover:text-ng-mid"
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { t } = useLanguage()
  const { itemCount, openCart } = useCart()
  const { shopEnabled } = useCatalog()
  const location = useLocation()
  const isHome = location.pathname === '/'
  const sectionHref = (id) => (isHome ? `#${id}` : `/#${id}`)
  const closeMobileMenu = () => setMenuOpen(false)

  const projectMenuItems = [
    { href: sectionHref('project'), label: t('nav.project') },
    { href: sectionHref('gites'), label: t('gites.kicker') },
    { href: sectionHref('orchards'), label: t('orchards.kicker') },
    { href: sectionHref('forest'), label: t('forest.kicker') },
    { href: sectionHref('founders'), label: t('aboutPage.members') },
  ]

  const discoverMenuItems = [
    { to: '/media', label: t('nav.media') },
    { to: '/partenaires', label: t('nav.partners') },
    { to: '/newsletter', label: t('nav.newsletter') },
  ]

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navBg = isHome
    ? scrolled ? 'bg-ng-beige/95 shadow-sm backdrop-blur-sm' : 'bg-ng-beige/88 backdrop-blur-sm'
    : 'bg-ng-beige/95 shadow-sm backdrop-blur-sm'

  return (
    <>
      <nav className={`fixed top-[var(--ng-staging-banner-height,0px)] left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">

            <Link to="/" className="flex items-center gap-2">
              <div className="h-10 w-10 overflow-hidden rounded-[10px] bg-[#f5efe2] p-0.5 shadow-sm ring-1 ring-black/5 lg:h-11 lg:w-11">
                <img
                  src="/logos/svg/logo.svg"
                  alt={t('brand.name')}
                  className="h-full w-full object-contain"
                />
              </div>
            </Link>

            <div className="hidden lg:flex items-center gap-5 xl:gap-6">
              <a
                href={sectionHref('home')}
                className="nav-link relative text-sm font-medium text-ng-dark hover:text-ng-mid transition-colors"
              >
                {t('common.home')}
              </a>

              <NavDropdown
                label={t('nav.project')}
                items={projectMenuItems}
              />

              <NavDropdown
                label={t('nav.discover')}
                items={discoverMenuItems}
              />

              {shopEnabled && (
                <Link
                  to="/boutique"
                  className="nav-link relative text-sm font-medium text-ng-dark hover:text-ng-mid transition-colors"
                >
                  {t('nav.shop')}
                </Link>
              )}

              <Link
                to="/contact"
                className="nav-link relative text-sm font-medium text-ng-dark hover:text-ng-mid transition-colors"
              >
                {t('nav.contact')}
              </Link>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {shopEnabled && (
                <button
                  type="button"
                  onClick={openCart}
                  className="relative rounded-lg p-2 text-ng-dark transition-colors hover:bg-ng-dark/5"
                  aria-label={t('cart.title')}
                >
                  <ShoppingBag className="h-5 w-5" />
                  {itemCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-ng-gold px-1 text-[10px] font-bold text-ng-dark shadow-sm">
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  )}
                </button>
              )}
              <LanguageSwitcher />
              <button
                type="button"
                className="lg:hidden p-2 text-ng-dark hover:bg-ng-dark/5 rounded-lg transition-colors"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label={menuOpen ? t('common.closeMenu') : t('common.openMenu')}
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden flex flex-col bg-white border-t border-ng-dark/10 px-6 py-4">
            <a
              href={sectionHref('home')}
              onClick={closeMobileMenu}
              className="py-3 text-base font-medium text-ng-dark border-b border-ng-dark/5 hover:text-ng-mid transition-colors"
            >
              {t('common.home')}
            </a>

            <MobileNavGroup
              label={t('nav.project')}
              items={projectMenuItems}
              onNavigate={closeMobileMenu}
            />

            <MobileNavGroup
              label={t('nav.discover')}
              items={discoverMenuItems}
              onNavigate={closeMobileMenu}
            />

            {shopEnabled && (
              <Link
                to="/boutique"
                onClick={closeMobileMenu}
                className="py-3 text-base font-medium text-ng-dark border-b border-ng-dark/5 hover:text-ng-mid transition-colors"
              >
                {t('nav.shop')}
              </Link>
            )}

            <Link
              to="/contact"
              onClick={closeMobileMenu}
              className="py-3 text-base font-medium text-ng-dark border-b border-ng-dark/5 hover:text-ng-mid transition-colors"
            >
              {t('nav.contact')}
            </Link>
          </div>
        )}
      </nav>
    </>
  )
}